import { useState, useMemo, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarDays, Filter, UserCheck } from "lucide-react";
import { format, addDays } from "date-fns";
import { es } from "date-fns/locale";
import DetailEditForm from "@/components/DetailEditForm";
import { generateSlots } from "@/utils/slots";

const DAY_MAP = ["domingo", "lunes", "martes", "miercoles", "jueves", "viernes", "sabado"];

const STATUS_CFG = {
  pendiente_confirmacion: { label: "Pte. Confirm.", text: "text-amber-800", bg: "bg-amber-50", border: "border-amber-400", dot: "bg-amber-500" },
  confirmada: { label: "Confirmada", text: "text-blue-800", bg: "bg-blue-50", border: "border-blue-500", dot: "bg-blue-500" },
  pendiente_reprogramacion: { label: "Reprogramar", text: "text-purple-800", bg: "bg-purple-50", border: "border-purple-400", dot: "bg-purple-500" },
  cancelada_cliente: { label: "Cancel. cliente", text: "text-red-700", bg: "bg-red-50", border: "border-red-400", dot: "bg-red-400" },
  cancelada_taller: { label: "Cancel. taller", text: "text-red-700", bg: "bg-red-50", border: "border-red-400", dot: "bg-red-400" },
  pendiente: { label: "Pendiente", text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-300", dot: "bg-amber-400" },
};

const CENTER_COLORS = [
  { bg: "bg-sky-50/60", dot: "bg-sky-500" },
  { bg: "bg-emerald-50/60", dot: "bg-emerald-500" },
  { bg: "bg-violet-50/60", dot: "bg-violet-500" },
  { bg: "bg-rose-50/60", dot: "bg-rose-500" },
];

export default function AdvisorView({ appointments, onRefresh, config, advisors }) {
  const { axiosAuth } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [detailApt, setDetailApt] = useState(null);
  const [centerFilter, setCenterFilter] = useState("todos");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [centersList, setCentersList] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/centers`).then(r => r.json())
      .then(d => setCentersList(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const centerColors = useMemo(() => {
    const map = {};
    centersList.forEach((c, i) => { map[c.slug] = CENTER_COLORS[i % CENTER_COLORS.length]; });
    return map;
  }, [centersList]);

  const interval = config?.appointment_interval || 20;
  const workingHours = config?.working_hours || {};
  const dayKey = DAY_MAP[currentDate.getDay()];
  const dayRange = workingHours[dayKey];
  const isClosed = !dayRange || dayRange.toLowerCase() === "cerrado";
  const dateStr = format(currentDate, "yyyy-MM-dd");

  const slots = useMemo(() => generateSlots(dayRange, interval), [dayRange, interval]);

  const activeAdvisors = useMemo(() => {
    let list = (advisors || []).filter(a => a.active);
    if (centerFilter !== "todos") list = list.filter(a => a.center === centerFilter);
    return list;
  }, [advisors, centerFilter]);

  // Columns: each advisor + "Sin asignar"
  const columns = useMemo(() => {
    const cols = activeAdvisors.map(a => ({ id: a.id, name: a.name, center: a.center }));
    cols.push({ id: "_none", name: "Sin asignar", center: "" });
    return cols;
  }, [activeAdvisors]);

  // Day's appointments filtered
  const dayApts = useMemo(() => {
    return appointments.filter(a => {
      if (a.date !== dateStr) return false;
      if (centerFilter !== "todos" && a.center !== centerFilter) return false;
      if (statusFilter !== "todos" && a.status !== statusFilter) return false;
      return true;
    });
  }, [appointments, dateStr, centerFilter, statusFilter]);

  // Group by advisor+slot
  const getApts = (advisorId, slot) => {
    return dayApts.filter(a => {
      if (advisorId === "_none") return !a.advisor_id && a.time_slot === slot;
      return a.advisor_id === advisorId && a.time_slot === slot;
    });
  };

  // Stats per advisor
  const advisorStats = useMemo(() => {
    const stats = {};
    columns.forEach(col => {
      const apts = dayApts.filter(a => col.id === "_none" ? !a.advisor_id : a.advisor_id === col.id);
      stats[col.id] = {
        total: apts.length,
        confirmed: apts.filter(a => a.status === "confirmada").length,
        pending: apts.filter(a => a.status === "pendiente_confirmacion").length,
      };
    });
    return stats;
  }, [columns, dayApts]);

  const goToday = () => setCurrentDate(new Date());
  const goPrev = () => setCurrentDate(d => addDays(d, -1));
  const goNext = () => setCurrentDate(d => addDays(d, 1));

  return (
    <div className="space-y-3" data-testid="advisor-view">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={goPrev} className="h-8 w-8 p-0 rounded-sm" data-testid="adv-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday} className="h-8 px-3 rounded-sm text-xs font-semibold uppercase" data-testid="adv-today">Hoy</Button>
          <Button variant="outline" size="sm" onClick={goNext} className="h-8 w-8 p-0 rounded-sm" data-testid="adv-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider" data-testid="adv-date-label">
          {format(currentDate, "EEEE d MMMM yyyy", { locale: es })}
        </h2>
        <div className="ml-auto flex items-center gap-2">
          <Select value={centerFilter} onValueChange={setCenterFilter}>
            <SelectTrigger className="h-8 w-40 rounded-sm text-xs" data-testid="adv-center-filter">
              <Filter className="w-3 h-3 mr-1" /><SelectValue placeholder="Centro" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los centros</SelectItem>
              {centersList.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-40 rounded-sm text-xs" data-testid="adv-status-filter">
              <Filter className="w-3 h-3 mr-1" /><SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              {Object.entries(STATUS_CFG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary strip */}
      <div className="flex items-center gap-3 text-xs text-slate-500">
        <span className="font-semibold text-slate-700">{dayApts.length} citas</span>
        {columns.slice(0, -1).map(col => {
          const s = advisorStats[col.id];
          return (
            <span key={col.id} className="flex items-center gap-1">
              <UserCheck className="w-3 h-3" />
              <strong>{col.name}:</strong> {s.total} ({s.confirmed} conf.)
            </span>
          );
        })}
        {advisorStats["_none"]?.total > 0 && (
          <span className="text-orange-600 font-semibold">Sin asignar: {advisorStats["_none"].total}</span>
        )}
      </div>

      {isClosed ? (
        <div className="text-center py-16 text-slate-400 text-sm">Este dia esta cerrado segun la configuracion horaria.</div>
      ) : (
        <div className="border border-slate-200 rounded-sm overflow-auto bg-white" style={{ maxHeight: "calc(100vh - 260px)" }}>
          <table className="w-full border-collapse" style={{ minWidth: `${80 + columns.length * 180}px` }}>
            <thead className="sticky top-0 z-20 bg-white">
              <tr>
                <th className="border-b border-r border-slate-200 p-1.5 text-center bg-slate-50 sticky left-0 z-30" style={{ width: 60 }}>
                  <span className="text-[10px] font-semibold uppercase text-slate-400">Hora</span>
                </th>
                {columns.map(col => {
                  const s = advisorStats[col.id];
                  const isUnassigned = col.id === "_none";
                  return (
                    <th key={col.id} className="border-b border-r last:border-r-0 border-slate-200 p-1.5 text-center bg-slate-50" style={{ minWidth: 170 }} data-testid={`adv-col-${col.id}`}>
                      <p className={`text-xs font-bold uppercase tracking-wider ${isUnassigned ? 'text-orange-600' : 'text-slate-800'}`}>{col.name}</p>
                      <p className="text-[9px] text-slate-400 mt-0.5">{s.total} citas &middot; {s.confirmed} conf.</p>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {slots.map(slot => (
                <tr key={slot} className="border-b border-slate-100">
                  <td className="border-r border-slate-200 p-1 text-center sticky left-0 bg-white z-10">
                    <span className="text-[10px] font-mono text-slate-400">{slot}</span>
                  </td>
                  {columns.map(col => {
                    const cellApts = getApts(col.id, slot);
                    return (
                      <td key={`${col.id}-${slot}`} className="border-r last:border-r-0 border-slate-100 p-px align-top" data-testid={`adv-cell-${col.id}-${slot}`}>
                        {cellApts.map(apt => {
                          const st = STATUS_CFG[apt.status] || STATUS_CFG.pendiente;
                          const cc = centerColors[apt.center] || CENTER_COLORS[0];
                          const isCancelled = apt.status?.includes("cancelada");
                          return (
                            <div key={apt.id}
                              onClick={() => setDetailApt(apt)}
                              className={`px-1.5 py-0.5 rounded-sm border-l-2 ${st.border} ${cc.bg} cursor-pointer hover:shadow-sm transition-shadow mb-px ${isCancelled ? 'opacity-40' : ''}`}
                              data-testid={`adv-apt-${apt.id}`}
                            >
                              <div className="flex items-center gap-1 min-w-0">
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${st.dot}`} />
                                <span className={`text-[10px] font-semibold ${st.text} truncate leading-tight`}>{apt.client_name}</span>
                              </div>
                              <div className="flex items-center gap-1 min-w-0">
                                {apt.plate && <span className="text-[8px] font-mono text-slate-500 truncate leading-tight">{apt.plate}</span>}
                                <span className="text-[8px] text-slate-400 truncate leading-tight">{apt.service_type}</span>
                              </div>
                              <div className="flex items-center gap-1 min-w-0">
                                <span className={`text-[8px] truncate leading-tight ${cc.dot ? 'text-slate-400' : ''}`}>{apt.center_name}</span>
                                <Badge className={`rounded-sm text-[7px] px-0.5 py-0 h-3 ${st.bg} ${st.text} border-0`}>{st.label}</Badge>
                              </div>
                            </div>
                          );
                        })}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detailApt} onOpenChange={() => setDetailApt(null)}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="adv-detail-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-red-600" /> Detalle de Cita
            </DialogTitle>
          </DialogHeader>
          {detailApt && <DetailEditForm apt={detailApt} config={config} onClose={() => setDetailApt(null)} onSave={async (updated) => {
            try {
              await axiosAuth().put(`/appointments/${detailApt.id}`, updated);
              toast.success("Cita actualizada. Se notificara al cliente si aplica.");
              setDetailApt(null);
              onRefresh();
            } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
          }} axiosAuth={axiosAuth} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
